import { ApplicationService, ContactService, DatabaseService, DataCollectionService, PortfolioService, ServerService
} from '@amzn/awscat-portfolio-assessment-service-client';
import { Contact, CreatedQuestionnaire, DeserializedApplication } from '@amzn/awscat-portfolio-assessment-types';
import { AuthContextInterface, FlashContextInterface, FlashType, withAuthContext, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Button, Container, DatePicker, Link, SpaceBetween, Tabs } from '@amzn/awsui-components-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { match, useHistory, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { DATE_PLACEHOLDER, toShortDate } from '../../../common/DateDisplay';
import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { ErrorWithResponse, MatchParam } from '../../../common/types';
import { convertDateStringToDate, generatePluralStr } from '../../../common/util';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import PreviewBanner from '../../common/PreviewBanner';
import { getApplicationContactColumnDefinition, getDatabaseContactColumnDefinition, getServerContactColumnDefinition, TabIds } from './constants';
import { getAllPortfolioUserEmails, getFutureDate } from './data-collection-helper';
import DataCollectionEditQuestionnaireModal from './DataCollectionEditQuestionnaireModal';
import DataCollectionEditQuestionnaireTemplateModal from './DataCollectionEditQuestionnaireTemplateModal';
import DataCollectionEmailNotificationModal from './DataCollectionEmailNotificationModal';
import { ResourceQuestionnaire } from './interfaces';

type Props = FlashContextInterface & AuthContextInterface;

const applicationService = new ApplicationService();
const serverService = new ServerService();
const databaseService = new DatabaseService();
const dataCollectionService = new DataCollectionService();
const contactService = new ContactService();
const portfolioService = new PortfolioService();

function DataCollection({ flash, auth }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });

    const portfolioId = match?.params?.portfolioId ?? '';
    const history = useHistory();
    const isMounted = useIsMounted();
    const { alert } = flash;

    const [ loadingApplications, setLoadingApplications ] = useState(false);
    const [ loadingServers, setLoadingServers ] = useState(false);
    const [ loadingDatabases, setLoadingDatabases ] = useState(false);
    const [ applicationContacts, setApplicationContacts ] = useState<ResourceQuestionnaire[]>([]);
    const [ serverContacts, setServerContacts ] = useState<ResourceQuestionnaire[]>([]);
    const [ databaseContacts, setDatabaseContacts ] = useState<ResourceQuestionnaire[]>([]);
    const [ contacts, setContacts ] = useState<Contact[]>([]);
    const [ selectedApplications, setSelectedApplications ] = useState<ResourceQuestionnaire[]>([]);
    const [ selectedServers, setSelectedServers ] = useState<ResourceQuestionnaire[]>([]);
    const [ selectedDatabases, setSelectedDatabases ] = useState<ResourceQuestionnaire[]>([]);
    const [ dueDate, setDueDate ] = useState<string>(toShortDate(getFutureDate(7)));
    const [ activeTab, setActiveTab ] = useState(TabIds.APPLICATION);
    const [ editQuestionnaireTemplateVisible, setEditQuestionnaireTemplateVisible ] = useState(false);
    const [ emailNotifyModalVisible, setEmailNotifyModalVisible ] = useState(false);
    const [ emails, SetEmails ] = useState<string[]>([]);
    const [ loadingSendRequest, setLoadingSendRequest ] = useState(false);
    const [ loadingCancelRequest, setLoadingCancelRequest ] = useState(false);
    const [ clickedResource, setClickedResource] = useState<ResourceQuestionnaire>();
    const [ editQuestionnaireVisible, setEditQuestionnaireVisible ] = useState(false);
    const { panelHandler } = useContext(HelpPanelContext);

    const loadApplications = useCallback(async () => {
        setLoadingApplications(true);
        try {
            const applications: DeserializedApplication[] = await applicationService.list(portfolioId);
            const questionnaireIds = applications.map((app: DeserializedApplication) => app.questionnaireId).filter(qId => qId);
            const questionnaires = await dataCollectionService.getBatch(portfolioId, questionnaireIds);
            const applicationContacts = applications.map(application => {
                const questionnaire = questionnaires.find(questionnaire => questionnaire.id === application.questionnaireId);
                const applicationContact: ResourceQuestionnaire = {
                    resourceId: application.applicationId,
                    resourceName: application.name,
                    ownerName: application.businessOwner,
                    ownerEmail: application.businessOwnerEmail,
                    ownerId: application.businessOwnerId,
                    ownerPhone: application.businessOwnerPhone,
                    questionnaireId: application.questionnaireId,
                    status: questionnaire?.status ?? constants.NOT_STARTED,
                    dueDate: questionnaire?.dueDate || '',
                };
                return applicationContact;
            });
            if (isMounted()) {
                setApplicationContacts(applicationContacts);
            }
        } catch (err) {
            if (isMounted()) {
                alert(FlashType.error, `${constants.UNABLE_TO_RETRIEVE} ${constants.APPLICATIONS_LOWERCASE}`, '');
            }
        } finally {
            if (isMounted()) {
                setLoadingApplications(false);
            }
        }
    }, [alert, isMounted, portfolioId]);

    const loadServers = useCallback(async () => {
        setLoadingServers(true);
        try {
            const servers = await serverService.list(portfolioId, true);
            const questionnaireIds = servers.map(server => server.questionnaireId).filter(qId => qId);
            const questionnaires = await dataCollectionService.getBatch(portfolioId, questionnaireIds);
            const serverContacts = servers.map(server => {
                const questionnaire = questionnaires.find(questionnaire => questionnaire.id === server.questionnaireId);
                const serverContact: ResourceQuestionnaire = {
                    resourceId: server.serverId,
                    ownerId: server.serverSupportId,
                    ownerEmail: server.serverSupportEmail,
                    ownerName: server.serverSupportName,
                    ownerPhone: server.serverSupportPhone,
                    questionnaireId: server.questionnaireId,
                    status: questionnaire?.status ?? constants.NOT_STARTED,
                    dueDate: questionnaire?.dueDate || '',
                };
                return serverContact;
            });
            if (isMounted()) {
                setServerContacts(serverContacts);
            }
        } catch (err) {
            if (isMounted()) {
                alert(FlashType.error, `${constants.UNABLE_TO_RETRIEVE} ${constants.SERVERS_LOWERCASE}`, '');
            }
        } finally {
            setLoadingServers(false);
        }
    }, [alert, isMounted, portfolioId]);

    const loadDatabases = useCallback(async () => {
        setLoadingDatabases(true);
        try {
            const databases = await databaseService.list(portfolioId);
            const questionnaireIds: string[] = databases.map(db => db.questionnaireId ?? '').filter(qId => !!qId);
            const questionnaires = await dataCollectionService.getBatch(portfolioId, questionnaireIds);
            const databaseContacts = databases.map(database => {
                const questionnaire = questionnaires.find(questionnaire => questionnaire.id === database.questionnaireId);
                const databaseContact: ResourceQuestionnaire = {
                    resourceId: database.databaseId,
                    ownerId: database.dbOwnerId ?? '',
                    ownerName: database.dbOwnerName ?? '',
                    ownerEmail: database.dbOwnerEmail ?? '',
                    ownerPhone: database.dbOwnerPhone ?? '',
                    questionnaireId: database.questionnaireId,
                    status: questionnaire?.status ?? constants.NOT_STARTED,
                    dueDate: questionnaire?.dueDate || '',
                };
                return databaseContact;
            });
            if (isMounted()) {
                setDatabaseContacts(databaseContacts);
            }
        } catch (err) {
            if (isMounted()) {
                alert(FlashType.error, `${constants.UNABLE_TO_RETRIEVE} ${constants.DATABASES_LOWERCASE}`, '');
            }
        } finally {
            if (isMounted()) {
                setLoadingDatabases(false);
            }
        }
    }, [alert, isMounted, portfolioId]);

    const loadContacts = useCallback(async () => {
        try {
            const contacts = await contactService.list(portfolioId);
            if (isMounted()) {
                setContacts(contacts);
            }
        } catch (err) {
            if (isMounted()) {
                alert(FlashType.error, `${constants.UNABLE_TO_RETRIEVE} ${constants.CONTACTS}`, '');
            }
        }
    }, [alert, isMounted, portfolioId]);

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.DATA_COLLECTION);
    }, [ panelHandler ]);

    useEffect(() => {
        loadServers();
        loadApplications();
        loadDatabases();
        loadContacts();
    }, [loadApplications, loadContacts, loadDatabases, loadServers]);

    const reloadResource = (resourceType: string) => {
        switch (resourceType) {
            case TabIds.APPLICATION:
                loadApplications();
                break;
            case TabIds.SERVER:
                loadServers();
                break;
            case TabIds.DATABASE:
                loadDatabases();
                break;
        }
    };

    const getSelectedRows = (resourceType: string): ResourceQuestionnaire[] => {
        switch (resourceType) {
            case TabIds.APPLICATION:
                return selectedApplications;
            case TabIds.SERVER:
                return selectedServers;
            case TabIds.DATABASE:
                return selectedDatabases;
            default:
                return [];
        }
    };

    const sendSelectedQuestionnaires = async () => {
        const resourceType = activeTab;
        setLoadingSendRequest(true);
        try {
            const selectedRows = getSelectedRows(resourceType);
            if (!selectedRows.length) {
                alert(FlashType.warning, constants.PLEASE_SELECT_A_QUESTIONNAIRE_TO_SEND, '');
                return;
            }

            if (new Date(dueDate) < new Date()) {
                alert(FlashType.warning, constants.DUE_DATE_MUST_BE_A_FUTURE_DATE, '');
                return;
            }

            const sentQuestionnaires = selectedRows.filter(resource => resource.status.toLowerCase() === 'sent');
            const unsentQuestionnaires = selectedRows.filter(resource => resource.status.toLowerCase() !== 'sent');
            const orphanedResources = unsentQuestionnaires.filter(resource => !resource.ownerId);
            const readyToSendQuestionnaires = unsentQuestionnaires.filter(resource => resource.ownerId);

            const needToCreateQuestionnaires = readyToSendQuestionnaires.filter(resource => !resource.questionnaireId)
                .map(resource => resource.resourceId);

            let alreadyCreatedQuestionnaireIds: string[] = readyToSendQuestionnaires.reduce((ids: string[], resource: ResourceQuestionnaire) => {
                if (resource.questionnaireId) {
                    ids.push(resource.questionnaireId);
                }
                return ids;
            }, []);

            if (orphanedResources.length > 0) {
                const msg = `${orphanedResources.length} ${generatePluralStr(orphanedResources.length, resourceType)}
                    ${orphanedResources.length > 1 ? constants.NO_OWNER : constants.NO_OWNER_PLURAL}`;
                alert(FlashType.warning, msg, constants.ERROR_NO_CONTACT);
                return;
            }

            if (sentQuestionnaires.length > 0) {
                const msg = `${sentQuestionnaires.length} ${generatePluralStr(sentQuestionnaires.length, constants.QUESTIONNAIRE)}
                    ${ sentQuestionnaires.length > 1 ? constants.ALREADY_SENT_PLURAL : constants.ALREADY_SENT}`;
                alert(FlashType.warning, msg, constants.ERROR_ALREADY_SENT);
                return;
            }
        
            let newlyCreatedQuestionnaires: CreatedQuestionnaire[] = [];
            if (needToCreateQuestionnaires.length > 0) {
                // get all the users who have access to portfolio - to add as questionnaire owners
                const userEmails = await getAllPortfolioUserEmails(auth, portfolioService, portfolioId);

                newlyCreatedQuestionnaires = await dataCollectionService.createBatch(portfolioId, 
                    { type: resourceType, dueDate: convertDateStringToDate(dueDate).toUTCString(),
                        resourceIds: needToCreateQuestionnaires, owners: userEmails });
                const newlyCreatedQuestionnaireIds = newlyCreatedQuestionnaires.map(questionnaire => questionnaire.questionnaire.id);
                alreadyCreatedQuestionnaireIds = alreadyCreatedQuestionnaireIds.concat(newlyCreatedQuestionnaireIds);
            }

            if (alreadyCreatedQuestionnaireIds.length > 0) {
                await dataCollectionService.send(portfolioId, alreadyCreatedQuestionnaireIds);
                reloadResource(resourceType);
                alert(FlashType.success, constants.QUESTIONNAIRES_SENT, '');
                const emails = getEmailsFromQuestionnaires(readyToSendQuestionnaires);
                if (emails) {
                    if (isMounted()) {
                        SetEmails(emails);
                        setEmailNotifyModalVisible(true);
                    }
                }
            }
        } catch (err) {
            if (isMounted()) {
                alert(FlashType.error, (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message,
                    constants.ERROR_UPDATING_QUESTIONNAIRE);
            }
        } finally {
            if (isMounted()) {
                setLoadingSendRequest(false);
            }
        }
    };

    const getEmailsFromQuestionnaires = (questionnaires: ResourceQuestionnaire[]): string[] => {
        const emails: string[] = [];

        questionnaires.forEach(questionnaire => {
            const contact = contacts.find(contact => contact.contactId === questionnaire.ownerId);
            if (contact) {
                emails.push(contact.email);
            }
        });
        return emails;
    };

    const deleteSelectedQuestionnaires = async () => {
        const resourceType = activeTab;
        setLoadingCancelRequest(true);
        try {
            const selectedRows = getSelectedRows(resourceType);
            if (!selectedRows.length) {
                alert(FlashType.warning, constants.PLEASE_SELECT_A_QUESTIONNAIRE_TO_DELETE, '');
                return;
            }

            const selectedResourcesAndQuestionnaires = selectedRows.reduce((resources, resource) => {
                if (resource.questionnaireId) {
                    resources.questionnaireIds.push(resource.questionnaireId);
                    resources.resourceIds.push(resource.resourceId);
                }
                return resources;
            }, {questionnaireIds: [] as string[], resourceIds: [] as string[], type: resourceType as string});

            await dataCollectionService.deleteMultiple(portfolioId, selectedResourcesAndQuestionnaires);
            reloadResource(resourceType);
            if (isMounted()) {
                alert(FlashType.success, constants.QUESTIONNAIRES_DELETED, '');
            }
        } catch (err) {
            if (isMounted()) {
                alert(FlashType.error, (err as Error).message, constants.ERROR_DELETING_QUESTIONNAIRE);
            }
        } finally {
            if (isMounted()) {
                setLoadingCancelRequest(false);
            }
        }
    };

    const editButtons = (
        <SpaceBetween size='m' direction='horizontal'>
            <Button
                onClick={() => history.push(`/portfolio/${portfolioId}/management/contact-management`)}
            >
                {constants.EDIT_CONTACT}
            </Button>
            <Button
                onClick={() => setEditQuestionnaireTemplateVisible(true)}
            > 
                {constants.EDIT_QUESTIONNAIRE_TEMPLATE}
            </Button>
        </SpaceBetween>
    );

    const requestActions = (
        <SpaceBetween size='s' direction='horizontal'>
            <Box>{constants.DUE_DATE}</Box>
            <DatePicker
                value={dueDate}
                nextMonthAriaLabel="Next month"
                placeholder={DATE_PLACEHOLDER}
                previousMonthAriaLabel="Previous month"
                todayAriaLabel="Today"
                onChange={({ detail }) => setDueDate(detail.value)}
            />
            <Button
                onClick={sendSelectedQuestionnaires}
                disabled={loadingCancelRequest}
                loading={loadingSendRequest}
            >
                {constants.SEND_REQUEST}
            </Button>
            <Button
                onClick={deleteSelectedQuestionnaires}
                disabled={loadingSendRequest}
                loading={loadingCancelRequest}
            >
                {constants.CANCEL_REQUEST}
            </Button>
        </SpaceBetween>
    );

    const getEmptyTableAlert = (message: JSX.Element): JSX.Element => {
        return (
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
                <Alert
                    header={constants.NO_DATA}
                >
                    {message}
                </Alert>
            </Box>
        );
    };

    const getEmptyApplicationTable = (): JSX.Element => {
        return getEmptyTableAlert(
            <Box>
                {constants.IMPORT_DATA_MSG_P1} {constants.BUSINESS_APPLICATION} {constants.DATA}. {constants.PLEASE_IMPORT_APPLICATIONS}
                <Link onFollow={() => history.push(`/portfolio/${portfolioId}/management/assets/applications`)}> {constants.BUSINESS_APPLICATION} </Link> 
                {constants.PAGE}.
            </Box>
        );
    };

    const getEmptyServerTable = (): JSX.Element => {
        return getEmptyTableAlert(
            <Box>
                {constants.IMPORT_DATA_MSG_P1} {constants.SERVER} {constants.DATA}. {constants.PLEASE_IMPORT_SERVERS}
                <Link onFollow={() => history.push(`/portfolio/${portfolioId}/management/assets/servers`)}> {constants.SERVER} </Link> 
                {constants.PAGE}.
            </Box>
        );
    };

    const getEmptyDatabaseTable = (): JSX.Element => {
        return getEmptyTableAlert(
            <Box>
                {constants.IMPORT_DATA_MSG_P1} {constants.DATABASE} {constants.DATA}. {constants.PLEASE_IMPORT_DATABASES}
                <Link onFollow={() => history.push(`/portfolio/${portfolioId}/management/assets/databases`)}> {constants.DATABASE} </Link> 
                {constants.PAGE}.
            </Box>
        );
    };

    const onRefreshAsset = () => {
        switch(activeTab) {
            case TabIds.APPLICATION:
                loadApplications();
                break;
            case TabIds.SERVER:
                loadServers();
                break;
            case TabIds.DATABASE:
                loadDatabases();
                break;
        }
    };

    const onResourceIdClicked = (resource: ResourceQuestionnaire) => () => {
        setClickedResource(resource);
        setEditQuestionnaireVisible(true);
    };

    const applicationContactColumnDefs = useMemo(() => getApplicationContactColumnDefinition(onResourceIdClicked), []);
    const applicationDataTable = (
        <AWSCATTable
            resourceName={constants.BUSINESS_APPLICATION}
            items={applicationContacts}
            columnDefinitions={applicationContactColumnDefs}
            loading={loadingApplications}
            selectionType='multi'
            onSelectionChange={setSelectedApplications as () => void}
            autoGenerateVisibleContent
            useDefaultDownload={true}
            emptyTable={getEmptyApplicationTable()}
            enableAutoSorting
            resizeable
        />
    );
    
    const serverContactColumnDefs = useMemo(() => getServerContactColumnDefinition(onResourceIdClicked), []);
    const serverDataTable = (
        <AWSCATTable
            loading={loadingServers}
            items={serverContacts}
            columnDefinitions={serverContactColumnDefs}
            resourceName={constants.SERVER_RESOURCE_NAME}
            selectionType='multi'
            onSelectionChange={setSelectedServers as () => void}
            autoGenerateVisibleContent
            useDefaultDownload={true}
            emptyTable={getEmptyServerTable()}
            enableAutoSorting
            resizeable
        />
    );

    const databaseContactColumnDefs =  useMemo(() => getDatabaseContactColumnDefinition(onResourceIdClicked), []);
    const databaseDataTable = (
        <AWSCATTable
            loading={loadingDatabases}
            items={databaseContacts}
            columnDefinitions={databaseContactColumnDefs}
            resourceName={constants.DATABASE_RESOURCE_NAME}
            selectionType='multi'
            onSelectionChange={setSelectedDatabases as () => void}
            autoGenerateVisibleContent
            useDefaultDownload={true}
            emptyTable={getEmptyDatabaseTable()}
            enableAutoSorting
            resizeable
        />
    );

    const assetTabs = (
        <Tabs
            tabs={[
                {
                    id: TabIds.APPLICATION,
                    label: constants.APPLICATIONS,
                    content: applicationDataTable,
                },
                {
                    id: TabIds.SERVER,
                    label: constants.SERVERS,
                    content: serverDataTable,
                },
                {
                    id: TabIds.DATABASE,
                    label: constants.DATABASES,
                    content: databaseDataTable,
                }
            ]}
            onChange={({ detail }) => setActiveTab(detail.activeTabId as TabIds)}
            activeTabId={activeTab}
        />
    );

    return (
        <Container>
            <SpaceBetween size='l'>
                <PreviewBanner />
                {editButtons}
                <Box float='right'>
                    {requestActions}
                </Box>
                {assetTabs}
                { editQuestionnaireTemplateVisible && (
                    <DataCollectionEditQuestionnaireTemplateModal
                        activeTab={activeTab}
                        visible={editQuestionnaireTemplateVisible}
                        setVisible={setEditQuestionnaireTemplateVisible}
                    /> 
                )}
                { emailNotifyModalVisible && (
                    <DataCollectionEmailNotificationModal
                        emails={emails}
                        visible={emailNotifyModalVisible}
                        setVisible={setEmailNotifyModalVisible}
                    />
                )}
                { editQuestionnaireVisible && (
                    <DataCollectionEditQuestionnaireModal
                        activeTab={activeTab}
                        resourceQuestionnaire={clickedResource}
                        visible={editQuestionnaireVisible}
                        setVisible={setEditQuestionnaireVisible}
                        dueDate={dueDate}
                        refreshAfterCreate={onRefreshAsset}
                    />
                )}
            </SpaceBetween>
        </Container>
    );
}

export default withAuthContext(withFlashContext(DataCollection));
